import useMutation from '../../../../../hooks/useMutation';
import React, { useContext, useState } from 'react';
import { UserContext } from '../../../../../context/UserContext';
import { ChangePasswordResponse, ChangePasswordVariables, UserMutations } from '../../../../../graphql/user.graphql';
import { useToast } from '../../../../../providers/ToastProvider';
import { TranslationKey } from '../../../../../types/translation/TranslationKey';
import { translate } from '../../../../../types/translation/Translator';
import { Button } from '../../../../components/Button';
import { Surface } from '../../../../components/Surface';
import { PasswordInput } from '../../../../components/TextInput';
import { ToastType } from '../../../../components/Toast';

interface ProfileSettingsPasswordViewProps {}

export default function ProfileSettingsPasswordView({}: ProfileSettingsPasswordViewProps) {
  const { signOut } = useContext(UserContext);

  const { showToast, closeToast } = useToast();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');

  const [update, { loading }] = useMutation<ChangePasswordResponse, ChangePasswordVariables>(
    UserMutations.changePassword,
    {
      variables: { oldPassword: currentPassword, newPassword },
      onCompleted: response => {
        setCurrentPassword('');
        setNewPassword('');
        showToast({
          type: ToastType.success,
          title: translate({ key: TranslationKey.changePasswordSuccessTitle }),
          content: id => (
            <p>
              Your changes have been applied and you will be signed out from other devices. You can try your new
              password by{' '}
              <span>
                <button
                  className='text-blue-600'
                  onClick={() => {
                    closeToast(id);
                    signOut();
                  }}
                >
                  logging out
                </button>
              </span>
              .
            </p>
          ),
        });
      },
    },
  );

  return (
    <Surface>
      <div className='grid gap-4'>
        <div className='grid gap-2'>
          <p className='section-title'>{translate({ key: TranslationKey.changePasswordSectionTitle })}</p>
          <p className='subtitle'>{translate({ key: TranslationKey.changePasswordSectionSubtitle })}</p>
        </div>
        <div className='r-grid'>
          <PasswordInput
            value={currentPassword}
            onChange={setCurrentPassword}
            label={translate({ key: TranslationKey.changePasswordCurrentPassword })}
            placeholder={translate({ key: TranslationKey.changePasswordCurrentPassword })}
          />
          <PasswordInput
            value={newPassword}
            onChange={setNewPassword}
            label={translate({ key: TranslationKey.changePasswordNewPassword })}
            validate
            placeholder={translate({ key: TranslationKey.changePasswordNewPassword })}
          />
        </div>
        <Button
          className='w-fit'
          text={translate({ key: TranslationKey.saveChanges })}
          onClick={() => update()}
          loading={loading}
          disabled={!currentPassword || !newPassword}
        />
      </div>
    </Surface>
  );
}
